import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionButton,
  Button,
  ComboBox,
  Content,
  DatePicker,
  Dialog,
  DialogTrigger,
  Flex,
  Footer,
  Form,
  Item,
  Link,
  Menu,
  MenuTrigger,
  ProgressCircle,
  Provider,
  TextArea,
  View,
} from '@adobe/react-spectrum';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  ArrowReturnRight,
  ArrowRepeat,
  Bell,
  Building,
  ChevronRight,
  Check2Circle,
  CircleFill,
  EnvelopeOpen,
  Envelope,
  EnvelopeArrowUp,
  FileEarmarkText,
  Funnel,
  Link45deg,
  PersonX,
  Plus,
  ZoomOut,
  ZoomIn,
} from 'react-bootstrap-icons';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { useViewInfo } from '../../../../hooks/UseViewInfo';
import { FPAData } from '../../../../infra/protected/FPA/FPAData';
import { invokeFinderApp } from '../../../../utils/NavigationUtils';
import { ListTimelineRequest } from '../../../../services/soap/features/requests/ListTimelineRequest';
import { NewMessageRequest } from '../../../../services/soap/message/requests/NewMessageRequest';
import { ListUserRequest } from '../../../../services/soap/access/requests/ListUserRequest';
import { ListFolderTypeRequest } from '../../../../services/soap/project/requests/ListFolderTypeRequest';
import { ListProjectTypeRequest } from '../../../../services/soap/project/requests/ListProjectTypeRequest';
import { ListActivityTypeRequest } from '../../../../services/soap/project/requests/ListActivityTypeRequest';
import {
  ENTRY_TYPE_OPTIONS as SHARED_ENTRY_TYPE_OPTIONS,
  MARKETING_TYPE_OPTIONS as SHARED_MARKETING_TYPE_OPTIONS,
  formatDetailValue as sharedFormatDetailValue,
  formatEntryDate as sharedFormatEntryDate,
  formatFieldName as sharedFormatFieldName,
  formatMonthHeader as sharedFormatMonthHeader,
  getContextTrail as sharedGetContextTrail,
  getDisplayTitle as sharedGetDisplayTitle,
  getLeftChipData as sharedGetLeftChipData,
  getRightChipText as sharedGetRightChipText,
  getTimelineVariant as sharedGetTimelineVariant,
  getVisibleDetailItems as sharedGetVisibleDetailItems,
  canExpandTimelineItem as sharedCanExpandTimelineItem,
  groupByCreatedMonth as sharedGroupByCreatedMonth,
  truncateText as sharedTruncateText,
} from '../../../../components/protected/layout-components/ReefTimeline/reefTimelineShared';
import styles from '../../issue/components/issue_timeline_view.module.css';

type TimelineItem = {
  id: string;
  name: string;
  valueOld: string;
  valueNew: string;
  description: string;
};

type TimelineChange = {
  id: string;
  created: string;
  createdByName: string;
  description: string;
  changeType: string;
  objectName: string;
  profileImage: string;
  title: string;
  status: string;
  statusColor: string;
  items: TimelineItem[];
  contextPath?: Array<{
    id: string;
    name: string;
    refId?: string;
    typeName?: string;
    level?: string;
  }>;
};

interface IContactTimelineViewProps {
  selectedItem?: FPAData;
  contactId: string;
  contactStatusList?: any[];
}

type FilterFormData = {
  userId: string;
  periodFrom: any;
  periodTo: any;
  entryType: Set<any>;
  marketingType: Set<any>;
  contextType: Set<any>;
};

type TimelineVariant = {
  key: string;
  icon: React.ReactNode;
  iconClassName: string;
  accentClassName?: string;
  showReloadBadge?: boolean;
  badgeIcon?: React.ReactNode;
  badgeClassName?: string;
  chipTone?: 'neutral' | 'status';
  supportsExpand?: boolean;
};

const PAGE_SIZE = 30;

const getScopedTimelineVariant = (item: TimelineChange): TimelineVariant => {
  const objectName = String(item.objectName || '').toLowerCase();

  if (objectName === 'message' || objectName === 'notice') {
    return {
      key: 'notice',
      icon: <Bell size={18} />,
      iconClassName: 'icon_generic',
      chipTone: 'neutral',
      supportsExpand: true,
    };
  }

  return sharedGetTimelineVariant(item as any);
};

const ENTRY_TYPE_OPTIONS = [
  { id: 'EMAIL', name: 'Email' },
  { id: 'NOTICE', name: 'Notice' },
  { id: 'MEETING', name: 'Meeting' },
  { id: 'MASSEMAIL', name: 'Massmail' },
  { id: 'MAILMERGE', name: 'Mailmerge' },
  { id: 'DOCUMENT', name: 'Document' },
  { id: 'WORKFLOW', name: 'Workflow' },
  { id: 'PHONECALL', name: 'Phonecall' },
  { id: 'LETTER', name: 'Letter' },
  { id: 'SMS', name: 'SMS' },
  { id: 'FORM', name: 'Form' },
  { id: 'ACTIVITY', name: 'Activity' },
  { id: 'PROJECT', name: 'Project' },
  { id: 'FOLDER', name: 'Folder' },
  { id: 'MESSAGE', name: 'Message' },
];

const MARKETING_TYPE_OPTIONS = [
  { id: 'GOAL-ACHIEVED', name: 'Goal achieved' },
  { id: 'UNSUBSCRIBE', name: 'Unsubscribe' },
  { id: 'MAIL-OPEN', name: 'Mail open' },
  { id: 'URL-VISIT', name: 'URL visit' },
  { id: 'MAIL-URL-VISIT', name: 'Mail URL visit' },
  { id: 'NEW-PHONE-NUMBER', name: 'New phone number' },
];

const normalizeTimelineItems = (itemsValue: any): TimelineItem[] => {
  const items = Array.isArray(itemsValue?.ITEM)
    ? itemsValue.ITEM
    : itemsValue?.ITEM
      ? [itemsValue.ITEM]
      : [];

  return items.map((item: any) => ({
    id: String(item?.id || ''),
    name: String(item?.name || ''),
    valueOld: String(item?.valueOld || ''),
    valueNew: String(item?.valueNew || ''),
    description: String(item?.description || ''),
  }));
};

const normalizeTimelineChanges = (changeValue: any): TimelineChange[] => {
  const changes = Array.isArray(changeValue) ? changeValue : changeValue ? [changeValue] : [];
  return changes.map((change: any) => {
    const items = normalizeTimelineItems(change?.ITEMS);
    const objectName = String(change?.objectName || '');
    const statusNameDetail = items.find((item) => item.name === 'customStateName');
    const statusColorDetail = items.find((item) => item.name === 'customStateColor');
    return {
      id: String(change?.id || ''),
      created: String(change?.created || ''),
      createdByName: String(change?.createdByName || ''),
      description: String(change?.description || ''),
      changeType: String(change?.changeType || ''),
      objectName,
      profileImage: String(change?.profileImage || ''),
      title: String(change?.description || change?.changeType || 'Timeline entry'),
      status:
        (objectName === 'FOLDER' || objectName === 'PROJECT' || objectName === 'ACTIVITY') && statusNameDetail
          ? String(statusNameDetail.valueNew || statusNameDetail.valueOld || statusNameDetail.description || '')
          : '',
      statusColor: statusColorDetail ? String(statusColorDetail.valueNew || statusColorDetail.valueOld || statusColorDetail.description || '') : '',
      items,
      contextPath: (Array.isArray(change?.CONTEXT_PATH?.CONTEXT) ? change.CONTEXT_PATH.CONTEXT : change?.CONTEXT_PATH?.CONTEXT ? [change.CONTEXT_PATH.CONTEXT] : []).map((node: any) => ({
        id: String(node?.id || ''),
        name: String(node?.name || ''),
        refId: node?.refId ? String(node.refId) : undefined,
        typeName: node?.typeName ? String(node.typeName) : undefined,
        level: node?.level ? String(node.level) : undefined,
      })),
    };
  });
};

const groupByCreatedMonth = (data: TimelineChange[]): Record<string, TimelineChange[]> => {
  return data.reduce((acc: Record<string, TimelineChange[]>, item) => {
    const createdDate = new Date(item.created);
    const monthYear = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;
    if (!acc[monthYear]) acc[monthYear] = [];
    acc[monthYear].push(item);
    return acc;
  }, {});
};

const formatMonthHeader = (value: string) => {
  const [year, month] = value.split('-');
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${monthNames[Number(month) - 1]} ${year}`;
};

const formatEntryDate = (dateString: string, t: any) => {
  const date = moment(dateString);
  if (date.isSame(moment(), 'day')) return t('today', { ns: 'layout_components', defaultValue: 'Today' });
  if (date.isSame(moment().subtract(1, 'day'), 'day')) return t('yesterday', { ns: 'layout_components', defaultValue: 'Yesterday' });
  return date.format('D.M. YYYY | HH:mm');
};

const formatFieldName = (value: string) => {
  const normalized = String(value || '').replace(/Value$/i, '');
  return normalized.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
};

const truncateText = (text: string, maxLength: number) => (
  text && text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
);

const normalizeLegacyColor = (value: string) => {
  if (!value) return '';
  const reversedHex = Number(value).toString(16).split('').reverse();
  const padding = Array(6 - reversedHex.length).fill('0');
  const hex = `#${[...reversedHex, ...padding].join('')}`;
  return hex === '#ffffff' ? '#FBFCF8' : hex;
};

const getPreferredStatusDetail = (item: TimelineChange) => {
  const statusNameDetail = item.items.find((detail) => detail.name === 'customStateName');
  if (statusNameDetail && (statusNameDetail.valueNew || statusNameDetail.valueOld || statusNameDetail.description)) {
    return statusNameDetail;
  }

  return item.items[0];
};

const getLeftChipData = (item: TimelineChange, variant: TimelineVariant) => {
  if (variant.key === 'status-update' && item.status) {
    return {
      text: 'New status',
      tone: 'status' as const,
      statusValue: item.status,
    };
  }

  if (variant.key === 'status-update') {
    const statusNameDetail = item.items.find((detail) => detail.name === 'customStateName');
    if (statusNameDetail && (statusNameDetail.valueNew || statusNameDetail.valueOld || statusNameDetail.description)) {
      return {
        text: 'New status',
        tone: 'status' as const,
        statusValue: String(statusNameDetail.valueNew || statusNameDetail.valueOld || statusNameDetail.description),
      };
    }
  }

  const firstDetail = variant.key === 'status-update' ? getPreferredStatusDetail(item) : item.items[0];
  if (!firstDetail) return null;

  const label = formatFieldName(firstDetail.name);
  const value = firstDetail.valueNew || firstDetail.valueOld || firstDetail.description;
  if (!value) {
    return {
      text: label,
      tone: variant.chipTone || 'neutral',
      statusValue: '',
    };
  }

  if (variant.chipTone === 'status') {
    return {
      text: label,
      tone: 'status' as const,
      statusValue: value,
    };
  }

  return {
    text: `${label}: ${value}`,
    tone: 'neutral' as const,
    statusValue: '',
  };
};

const getRightChipText = (item: TimelineChange, variant: TimelineVariant) => {
  const haystack = `${item.title} ${item.description} ${item.changeType}`.toLowerCase();
  const firstDetail = item.items[0];
  const visitValue = firstDetail?.valueNew || firstDetail?.valueOld || '1';

  if (variant.key === 'email-opened') {
    return `Opened ${visitValue}x`;
  }

  if (variant.key === 'email-visited') {
    return `URL visited ${visitValue}x`;
  }

  return haystack.includes('opened') ? 'Opened 1x' : '';
};

const getContextTrail = (item: TimelineChange, t: any) => {
  const firstDetail = item.items[0];
  const left = item.createdByName || t('contact_timeline.system', { ns: 'contacts', defaultValue: 'System' });
  const right = firstDetail?.description
    || (firstDetail ? formatFieldName(firstDetail.name) : '')
    || item.changeType
    || t('contact_detail.contact', { ns: 'contacts', defaultValue: 'Contact' });
  return { left, right };
};

const getDisplayTitle = (item: TimelineChange, variant: TimelineVariant) => {
  const firstDetail = item.items[0];
  const detailValue = firstDetail?.valueNew || firstDetail?.valueOld || '';
  const description = String(item.description || item.title || '').trim();

  if (variant.key === 'email-sent') {
    return detailValue ? `Sent to: ${detailValue}` : description || 'Sent message';
  }

  if (variant.key === 'email-received') {
    return detailValue ? `From: ${detailValue}` : description || 'Received message';
  }

  if (variant.key === 'email-opened') {
    return detailValue ? `From: ${detailValue}` : description || 'Mail open';
  }

  if (variant.key === 'email-visited') {
    return detailValue ? `Recipient: ${detailValue}` : description || 'URL visited';
  }

  return description || item.title || 'Timeline entry';
};

const getTimelineVariant = (item: TimelineChange): TimelineVariant => {
  const haystack = `${item.title} ${item.description} ${item.changeType} ${item.items.map((entry) => `${entry.name} ${entry.valueNew}`).join(' ')}`.toLowerCase();
  const hasCustomStateChange = item.items.some((entry) => entry.name === 'customState' || entry.name === 'customStateName');

  if (haystack.includes('url') || haystack.includes('visit')) {
    return {
      key: 'email-visited',
      icon: <Envelope size={18} />,
      iconClassName: styles.icon_mail,
      badgeIcon: <Link45deg size={9} />,
      badgeClassName: styles.badge_info,
      chipTone: 'neutral',
      supportsExpand: false,
    };
  }

  if (haystack.includes('opened')) {
    return {
      key: 'email-opened',
      icon: <EnvelopeOpen size={18} />,
      iconClassName: styles.icon_mail_opened,
      badgeIcon: <EnvelopeOpen size={9} />,
      badgeClassName: styles.badge_info,
      chipTone: 'neutral',
      supportsExpand: false,
    };
  }

  if (haystack.includes('sent') || haystack.includes('outgoing')) {
    return {
      key: 'email-sent',
      icon: <EnvelopeArrowUp size={18} />,
      iconClassName: styles.icon_sent,
      accentClassName: styles.accent_success,
      chipTone: 'neutral',
      supportsExpand: true,
    };
  }

  if (haystack.includes('mail') || haystack.includes('email')) {
    return {
      key: 'email-received',
      icon: <Envelope size={18} />,
      iconClassName: styles.icon_mail,
      chipTone: 'neutral',
      supportsExpand: true,
    };
  }

  if (haystack.includes('form')) {
    return {
      key: 'form-update',
      icon: <FileEarmarkText size={18} />,
      iconClassName: styles.icon_form,
      showReloadBadge: true,
      accentClassName: styles.accent_warning,
      chipTone: 'neutral',
      supportsExpand: true,
    };
  }

  if (haystack.includes('status') || hasCustomStateChange) {
    return {
      key: 'status-update',
      icon: <Building size={18} />,
      iconClassName: styles.icon_corporate,
      showReloadBadge: true,
      accentClassName: styles.accent_warning,
      chipTone: 'status',
      supportsExpand: false,
    };
  }

  if (haystack.includes('goal') || haystack.includes('achieved')) {
    return {
      key: 'goal-achieved',
      icon: <Check2Circle size={18} />,
      iconClassName: styles.icon_goal,
      chipTone: 'neutral',
      supportsExpand: false,
    };
  }

  if (haystack.includes('unsubscribe') || haystack.includes('cancel')) {
    return {
      key: 'unsubscribe',
      icon: <PersonX size={18} />,
      iconClassName: styles.icon_danger,
      chipTone: 'neutral',
      supportsExpand: false,
    };
  }

  return {
    key: 'generic',
    icon: <Bell size={18} />,
    iconClassName: styles.icon_generic,
    chipTone: 'neutral',
    supportsExpand: false,
  };
};

const ContactTimelineView: React.FC<IContactTimelineViewProps> = ({ selectedItem, contactId, contactStatusList }) => {
  const { t } = useTranslation(['contacts', 'layout_components']);
  const { featureService, store, accessService, projectService, messageService } = useDependency();
  const { isMobile } = useViewInfo();
  const [entries, setEntries] = useState<TimelineChange[]>([]);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [userData, setUserData] = useState<any[]>([]);
  const [contextTypeOptions, setContextTypeOptions] = useState<any[]>([]);
  const [requestParams, setRequestParams] = useState<any>({});
  const [formValue, setFormValue] = useState<FilterFormData>({
    userId: '',
    periodFrom: null,
    periodTo: null,
    entryType: new Set(),
    marketingType: new Set(),
    contextType: new Set(),
  });
  const newComment = useRef('');
  const [commentRecipients, setCommentRecipients] = useState<Set<any>>(new Set());

  const loadTimeline = useCallback(async (nextOffset = 0, append = false, extraParams: any = requestParams) => {
    if (!contactId) {
      setEntries([]);
      setOffset(0);
      setTotalCount(0);
      return;
    }

    setIsLoading(true);
    try {
      const extraFilterCols = Array.isArray(extraParams?.FILTER?.COL) ? extraParams.FILTER.COL : [];
        const response: any = await featureService.listTimeline(
          new ListTimelineRequest(store.Server, store.SessionId, {
            limit: PAGE_SIZE,
            offset: nextOffset,
            contextRoot: selectedItem?.id,
          includeSlave: 'false',
          FILTER: {
            COL: [
              {
                name: 'contact',
                type: 'in',
                value: contactId,
              },
              ...extraFilterCols,
            ],
          },
          ORDER: {
            COL: [{ name: 'created', desc: 'true' }],
            },
          })
        );

        const normalized = normalizeTimelineChanges(response?.CHANGE);
      setEntries((prev) => (append ? [...prev, ...normalized] : normalized));
      setOffset(nextOffset + normalized.length);
      setTotalCount(Number(response?.count || 0));
    } finally {
      setIsLoading(false);
    }
  }, [contactId, featureService, requestParams, selectedItem?.id, store.Server, store.SessionId]);

  useEffect(() => {
    setEntries([]);
    setExpandedIds({});
    setOffset(0);
    setTotalCount(0);
    void loadTimeline(0, false, requestParams);
  }, [contactId, selectedItem?.id, loadTimeline, requestParams]);

  useEffect(() => {
    (async () => {
      try {
        const countResponse = await accessService.listUser(new ListUserRequest(store.Server, store.SessionId, true, undefined, undefined, '1'));
        const totalUsers = countResponse?.result === 'OK' ? countResponse.count : 0;
        const userResp = await accessService.listUser(new ListUserRequest(store.Server, store.SessionId, true, totalUsers));
        if (userResp?.result === 'OK') {
          setUserData(
            userResp.USERS.map(({ USER }: any) => ({
              id: USER.id,
              name: `${USER.name1} ${USER.name3}`,
            }))
          );
        }

        const [folderTypes, projectTypes, activityTypes] = await Promise.all([
          projectService.listFolderType(new ListFolderTypeRequest(store.Server, store.SessionId, true)).then((res: any) => res.TYPE_LIST || []),
          projectService.listProjectType(new ListProjectTypeRequest(store.Server, store.SessionId)).then((res: any) => res.TYPE_LIST || []),
          projectService.listActivityType(new ListActivityTypeRequest(store.Server, store.SessionId, {})).then((res: any) => res.TYPE || []),
        ]);

        const extractTypeData = (types: any[], key: string) =>
          types.map((type: any) => ({
            id: key ? (type[key]?.id || type.id) : type.id,
            name: key ? (type[key]?.name || type.name) : type.name,
          }));

        setContextTypeOptions([
          ...extractTypeData(folderTypes, 'TYPE'),
          ...extractTypeData(projectTypes, 'TYPE'),
          ...extractTypeData(activityTypes, ''),
        ]);
      } catch {
        // Keep filter options empty on failure.
      }
    })();
  }, [accessService, projectService, store.Server, store.SessionId]);

  const groupedData = useMemo(() => sharedGroupByCreatedMonth(entries), [entries]);
  const headers = useMemo(() => Object.keys(groupedData).map((title) => ({ title })), [groupedData]);
  const canLoadMore = headers.length > 0;

  const toggleVisibility = (id: string) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const reloadTimeline = async (params: any = requestParams) => {
    setEntries([]);
    setExpandedIds({});
    setOffset(0);
    setTotalCount(0);
    await loadTimeline(0, false, params);
  };

  const clearFilters = async () => {
    setFormValue({
      userId: '',
      periodFrom: null,
      periodTo: null,
      entryType: new Set(),
      marketingType: new Set(),
      contextType: new Set(),
    });
    setRequestParams({});
  };

  const applyFilters = async () => {
    const periodFrom = formValue.periodFrom && moment(new Date(formValue.periodFrom)).format('YYYY-MM-DD');
    const periodTo = formValue.periodTo && moment(new Date(formValue.periodTo)).format('YYYY-MM-DD');
    const entryMarketingType = `${Array.from(formValue.marketingType).join(',')},${Array.from(formValue.entryType).join(',')}`.replace(/(^,|,$)/g, '');
    const contextType = Array.from(formValue.contextType).join(', ');

    const filterCols: any[] = [
      periodFrom ? { name: 'created', type: 'greaterOrEquals', value: periodFrom } : null,
      periodTo ? { name: 'created', type: 'lessOrEquals', value: periodTo } : null,
      formValue.userId ? { name: 'createdBy', type: 'in', value: formValue.userId } : null,
      entryMarketingType ? { name: 'objectName', type: 'in', value: entryMarketingType } : null,
      contextType ? { name: 'contextType', type: 'in', value: contextType } : null,
    ].filter(Boolean);

    setRequestParams(filterCols.length > 0 ? { FILTER: { COL: filterCols } } : {});
  };

  const addNewComment = async () => {
    if (!selectedItem?.id || newComment.current.trim() === '') return;

    const recipientUsers = Array.from(commentRecipients)
      .map((userId) => userData.find((user: any) => String(user.id) === String(userId)))
      .filter(Boolean)
      .map((user: any) => ({
        CONTENT: user.name,
        contact: user.name,
        id: String(user.id),
      }));

    await messageService.newMessage(
      new NewMessageRequest(store.Server, store.SessionId, {
        numberOfAttachments: 0,
        priority: 2,
        type: 1,
        contextId: selectedItem.id,
        schedulerId: 0,
        SUBJECT: {
          string: newComment.current,
        },
        RECIPIENT: recipientUsers.length > 0 ? { USER: recipientUsers } : {},
        COMMENT: {
          string: newComment.current,
        },
        CONTACT_RELATIONS: {},
      })
    );

    newComment.current = '';
    setCommentRecipients(new Set());
    await reloadTimeline();
  };

  if (!contactId) {
    return (
      <View UNSAFE_className={styles.parent_container}>
        <View UNSAFE_className={styles.empty_state}>
          {t('no_timeline_entries', { ns: 'layout_components' })}
        </View>
      </View>
    );
  }

  return (
    <div className={styles.parent_container}>
      <div className={styles.timeline_header}>
        <div className={styles.timeline_title}>
          {t('timeline', { ns: 'layout_components' })}
        </div>
        <div className={styles.timeline_toolbar}>
          <ActionButton
            isQuiet
            UNSAFE_className={styles.header_icon_button}
            aria-label={t('refresh', { ns: 'layout_components', defaultValue: 'Refresh' })}
            onPress={() => void reloadTimeline()}
          >
            <ArrowRepeat size={18} />
          </ActionButton>
          <DialogTrigger type="popover">
            <ActionButton isQuiet UNSAFE_className={styles.header_icon_button} aria-label={t('filter', { ns: 'layout_components', defaultValue: 'Filter' })}>
              <Funnel size={18} />
            </ActionButton>
            {(close) => (
              <Dialog size="M">
                <Content>
                  <Form>
                    <Flex direction={isMobile ? 'column' : 'row'} gap="size-100">
                      <Provider locale="en-US">
                        <DatePicker
                          autoFocus
                          label={t('period_from', { ns: 'layout_components', defaultValue: 'Period from' })}
                          width="100%"
                          value={formValue.periodFrom}
                          onChange={(value: any) => setFormValue((prev) => ({ ...prev, periodFrom: value }))}
                          UNSAFE_style={{ backgroundColor: 'white' }}
                        />
                      </Provider>
                      <Provider locale="en-US">
                        <DatePicker
                          label={t('period_to', { ns: 'layout_components', defaultValue: 'Period to' })}
                          width="100%"
                          value={formValue.periodTo}
                          onChange={(value: any) => setFormValue((prev) => ({ ...prev, periodTo: value }))}
                          UNSAFE_style={{ backgroundColor: 'white' }}
                        />
                      </Provider>
                    </Flex>
                    <ComboBox
                      width="100%"
                      label={t('user', { ns: 'layout_components', defaultValue: 'User' })}
                      selectedKey={formValue.userId}
                      defaultItems={userData}
                      onSelectionChange={(value) => setFormValue((prev) => ({ ...prev, userId: value as string }))}
                    >
                      {(item: any) => <Item key={`user_id_${item.id}`}>{item.name}</Item>}
                    </ComboBox>
                    <MenuTrigger>
                      <ActionButton aria-label={t('type_of_entry', { ns: 'layout_components', defaultValue: 'Type of entry' })}>
                        {t('type_of_entry', { ns: 'layout_components', defaultValue: 'Type of entry' })}
                      </ActionButton>
                      <Menu
                        width="100%"
                        items={SHARED_ENTRY_TYPE_OPTIONS}
                        selectedKeys={formValue.entryType}
                        onSelectionChange={(value) => setFormValue((prev) => ({ ...prev, entryType: value as Set<any> }))}
                        selectionMode="multiple"
                      >
                        {(item: any) => <Item key={`entry_type_${item.id}`}>{item.name}</Item>}
                      </Menu>
                    </MenuTrigger>
                    <MenuTrigger>
                      <ActionButton aria-label={t('marketing_trigger', { ns: 'layout_components', defaultValue: 'Marketing trigger' })}>
                        {t('marketing_trigger', { ns: 'layout_components', defaultValue: 'Marketing trigger' })}
                      </ActionButton>
                      <Menu
                        items={SHARED_MARKETING_TYPE_OPTIONS}
                        selectedKeys={formValue.marketingType}
                        onSelectionChange={(value) => setFormValue((prev) => ({ ...prev, marketingType: value as Set<any> }))}
                        selectionMode="multiple"
                      >
                        {(item: any) => <Item key={`marketing_type_${item.id}`}>{item.name}</Item>}
                      </Menu>
                    </MenuTrigger>
                    <MenuTrigger>
                      <ActionButton aria-label={t('context_type', { ns: 'layout_components', defaultValue: 'Context type' })}>
                        {t('context_type', { ns: 'layout_components', defaultValue: 'Context type' })}
                      </ActionButton>
                      <Menu
                        items={contextTypeOptions}
                        selectedKeys={formValue.contextType}
                        onSelectionChange={(value) => setFormValue((prev) => ({ ...prev, contextType: value as Set<any> }))}
                        selectionMode="multiple"
                      >
                        {(item: any) => <Item key={`context_type_${item.id}`}>{item.name}</Item>}
                      </Menu>
                    </MenuTrigger>
                  </Form>
                </Content>
                <Footer UNSAFE_style={{ paddingTop: '20px' }}>
                  <Flex direction={isMobile ? 'column' : 'row'} width="100%" gap="size-100">
                    <Button
                      width={isMobile ? '100%' : '30%'}
                      variant="primary"
                      onPress={async () => {
                        await clearFilters();
                        close();
                      }}
                      UNSAFE_style={{ borderRadius: '8px', color: '#1976d2', borderColor: '#1976d2' }}
                    >
                      {t('clear', { ns: 'layout_components', defaultValue: 'Clear' })}
                    </Button>
                    <Button
                      width={isMobile ? '100%' : '70%'}
                      variant="accent"
                      onPress={async () => {
                        await applyFilters();
                        close();
                      }}
                      UNSAFE_style={{ borderRadius: '8px' }}
                    >
                      {t('apply', { ns: 'layout_components', defaultValue: 'Apply' })}
                    </Button>
                  </Flex>
                </Footer>
              </Dialog>
            )}
          </DialogTrigger>
          <DialogTrigger type="popover">
            <ActionButton
              isQuiet
              UNSAFE_className={styles.header_icon_button}
              aria-label={t('new_comment', { ns: 'layout_components', defaultValue: 'New comment' })}
              isDisabled={!selectedItem?.id}
            >
              <Plus size={18} />
            </ActionButton>
            {(close) => (
              <Dialog size="M">
                  <Content>
                    <Form>
                      <TextArea
                      isRequired
                      autoFocus
                      label={t('new_comment', { ns: 'layout_components', defaultValue: 'New comment' })}
                        onChange={(value: any) => {
                          newComment.current = value;
                        }}
                      />
                      <MenuTrigger>
                        <ActionButton aria-label={t('notify', { ns: 'layout_components', defaultValue: 'Notify' })}>
                          {commentRecipients.size > 0
                            ? `${t('notify', { ns: 'layout_components', defaultValue: 'Notify' })}: ${commentRecipients.size}`
                            : t('notify', { ns: 'layout_components', defaultValue: 'Notify' })}
                        </ActionButton>
                        <Menu
                          items={userData}
                          selectedKeys={commentRecipients}
                          onSelectionChange={(value) => setCommentRecipients(value as Set<any>)}
                          selectionMode="multiple"
                        >
                          {(item: any) => <Item key={`comment_user_${item.id}`}>{item.name}</Item>}
                        </Menu>
                      </MenuTrigger>
                    </Form>
                  </Content>
                <Footer>
                  <Button
                    width="100%"
                    variant="accent"
                    onPress={async () => {
                      await addNewComment();
                      close();
                    }}
                    UNSAFE_style={{ borderRadius: '8px' }}
                  >
                    {t('add_comment', { ns: 'layout_components', defaultValue: 'Add comment' })}
                  </Button>
                </Footer>
              </Dialog>
            )}
          </DialogTrigger>
        </div>
      </div>

      {isLoading && entries.length === 0 ? (
        <div className={styles.loading_state}>
          <ProgressCircle
            aria-label={`${t('loading', { ns: 'layout_components', defaultValue: 'Loading' })} ${t('timeline', { ns: 'layout_components' })}`}
            isIndeterminate
          />
        </div>
      ) : headers.length > 0 ? (
        <div className={styles.timeline_content}>
          {headers.map((header, headerIndex) => (
            <div key={`header_${headerIndex}_${header.title}`} className={styles.month_group}>
              <div className={styles.date_header_parent}>
                <b className={styles.date_header_text}>{sharedFormatMonthHeader(header.title)}</b>
              </div>

              <div className={styles.month_entries}>
                <div className={styles.month_rail} />
                {groupedData[header.title]?.map((item, itemIndex) => {
                  const isExpanded = !!expandedIds[item.id];
                  const variant = getScopedTimelineVariant(item);
                  const leftChip = sharedGetLeftChipData(item, variant);
                  const rightChip = sharedGetRightChipText(item, variant);
                  const contextTrail = sharedGetContextTrail(
                    item,
                    t,
                    'contact_timeline.system',
                    t('contact_detail.contact', { ns: 'contacts', defaultValue: 'Contact' }),
                    selectedItem?.id
                  );
                  const displayTitle = sharedGetDisplayTitle(item, variant);
                  const visibleDetailItems = sharedGetVisibleDetailItems(item, variant);
                  const showDetailAction = sharedCanExpandTimelineItem(item, variant, visibleDetailItems);
                  const detailBoxClassName = [
                    styles.detail_box,
                    variant.key === 'form-update' ? styles.detail_box_form : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  const detailRowClassName = [
                    styles.detail_row,
                    variant.key === 'form-update' ? styles.detail_row_form : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  const detailLabelClassName = [
                    styles.detail_label,
                    variant.key === 'form-update' ? styles.detail_label_form : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  const detailValueClassName = [
                    styles.detail_value,
                    variant.key === 'form-update' ? styles.detail_value_form : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  const resolvedStatusColor =
                    contactStatusList?.find((status: any) => status.name === item.status)?.color || normalizeLegacyColor(item.statusColor);
                  const useDynamicStatusAccent =
                    !!resolvedStatusColor && (variant.key === 'status-update' || leftChip?.tone === 'status');
                  const cardClassName = [
                    styles.card,
                    variant.accentClassName ? styles[variant.accentClassName] || '' : '',
                    useDynamicStatusAccent ? styles.accent_dynamic : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  const cardStyle =
                    useDynamicStatusAccent
                      ? ({ ['--timeline-accent-color' as any]: resolvedStatusColor } as React.CSSProperties)
                      : undefined;

                  return (
                    <div key={`timeline_item_${item.id}_${itemIndex}`} className={styles.timeline_row}>
                      <div className={styles.marker_column}>
                        <div className={styles.marker_outer}>
                          <div className={`${styles.marker_inner} ${styles[variant.iconClassName] || ''}`}>
                            {variant.icon}
                          </div>
                          {variant.showReloadBadge ? (
                            <div className={styles.reload_badge}>
                              <ArrowRepeat size={8} />
                            </div>
                          ) : variant.badgeIcon ? (
                            <div className={`${styles.corner_badge} ${variant.badgeClassName ? styles[variant.badgeClassName] || '' : ''}`}>
                              {variant.badgeIcon}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className={cardClassName} style={cardStyle}>
                        <div className={styles.card_time}>{sharedFormatEntryDate(item.created, t)}</div>
                        <div className={styles.card_user} title={item.createdByName}>
                          {sharedTruncateText(item.createdByName, 22)}
                        </div>

                        <div
                          className={[
                            styles.card_body,
                            rightChip || showDetailAction ? styles.card_body_with_meta : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <div className={styles.card_title} title={displayTitle}>
                            {sharedTruncateText(displayTitle, 120)}
                          </div>

                          {contextTrail.left || contextTrail.right ? (
                            <div
                              className={[
                                styles.card_context,
                                contextTrail.targetId ? styles.card_context_clickable : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              onClick={contextTrail.targetId ? () => invokeFinderApp(contextTrail.targetId) : undefined}
                              onKeyDown={contextTrail.targetId ? (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  invokeFinderApp(contextTrail.targetId);
                                }
                              } : undefined}
                              role={contextTrail.targetId ? 'button' : undefined}
                              tabIndex={contextTrail.targetId ? 0 : undefined}
                            >
                              {contextTrail.left ? <span>{sharedTruncateText(contextTrail.left, 48)}</span> : null}
                              {contextTrail.left && contextTrail.right ? <ChevronRight size={12} className={styles.context_chevron} /> : null}
                              {contextTrail.right ? <span>{sharedTruncateText(contextTrail.right, 40)}</span> : null}
                            </div>
                          ) : null}

                          {leftChip || rightChip || showDetailAction ? (
                            <div className={styles.card_chip_row}>
                              <div className={styles.card_chip_left}>
                                {leftChip ? (
                                  <div
                                    className={`${styles.card_chip} ${leftChip.tone === 'status' ? styles.card_chip_status : ''}`}
                                    title={leftChip.tone === 'status' ? `${leftChip.text}: ${leftChip.statusValue}` : leftChip.text}
                                  >
                                    {leftChip.tone === 'status' ? (
                                      <>
                                        <span className={styles.card_chip_label}>{leftChip.text}:</span>
                                        <span className={styles.status_value_wrap}>
                                          <CircleFill
                                            size={8}
                                            className={styles.status_dot}
                                            style={resolvedStatusColor ? { color: resolvedStatusColor } : undefined}
                                          />
                                          <span>{sharedTruncateText(leftChip.statusValue, 20)}</span>
                                        </span>
                                      </>
                                    ) : (
                                      sharedTruncateText(leftChip.text, showDetailAction || rightChip ? 56 : 76)
                                    )}
                                  </div>
                                ) : null}
                              </div>
                              <div className={styles.card_chip_right}>
                                {rightChip ? (
                                  <div className={styles.card_chip_secondary} title={rightChip}>
                                    {sharedTruncateText(rightChip, 18)}
                                  </div>
                                ) : null}
                                {showDetailAction ? (
                                  <button
                                    type="button"
                                    className={styles.detail_icon}
                                    onClick={() => toggleVisibility(item.id)}
                                    aria-expanded={isExpanded}
                                    aria-label={isExpanded
                                      ? t('hide_details', { ns: 'layout_components' })
                                      : t('show_details', { ns: 'layout_components' })}
                                  >
                                    {isExpanded ? <ZoomOut size={16} /> : <ZoomIn size={16} />}
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          ) : null}

                          {isExpanded && visibleDetailItems.length > 0 ? (
                            <div className={detailBoxClassName}>
                              {visibleDetailItems.map((detail) => (
                                <div
                                  key={detail.id || `${item.id}-${detail.name}`}
                                  className={[
                                    detailRowClassName,
                                    (() => {
                                      const detailLabel = sharedFormatFieldName(detail.name);
                                      const isSubjectDetail = String(detail.name || '').toLowerCase() === 'subject';
                                      return !detailLabel || isSubjectDetail ? styles.detail_row_full : '';
                                    })(),
                                  ].filter(Boolean).join(' ')}
                                >
                                  {(() => {
                                    const detailLabel = sharedFormatFieldName(detail.name);
                                    const isSubjectDetail = String(detail.name || '').toLowerCase() === 'subject';
                                    const emphasizedLabels = ['To', 'Mail To', 'Cc', 'Bcc'];
                                    const showOutgoingArrow = detailLabel === 'To' || detailLabel === 'Mail To';
                                    const resolvedDetailLabelClassName = [
                                      detailLabelClassName,
                                      emphasizedLabels.includes(detailLabel) ? styles.detail_label_emphasis : '',
                                      variant.key === 'form-update' && detailLabel === 'From' ? styles.detail_label_form_from : '',
                                      variant.key === 'form-update' && detailLabel === 'To' ? styles.detail_label_form_to : '',
                                    ]
                                      .filter(Boolean)
                                      .join(' ');

                                    if (!detailLabel || isSubjectDetail) return null;

                                    return (
                                      <div className={resolvedDetailLabelClassName}>
                                        <span>{detailLabel}</span>
                                        {showOutgoingArrow ? <ArrowReturnRight size={12} style={{ transform: 'scaleY(-1)' }} /> : null}
                                      </div>
                                    );
                                  })()}
                                  <div
                                    className={[
                                      detailValueClassName,
                                      ['email-sent', 'email-received', 'email-opened', 'email-visited'].includes(variant.key)
                                      && String(detail.name || '').toLowerCase() === 'subject'
                                        ? styles.detail_value_subject
                                        : '',
                                    ].filter(Boolean).join(' ')}
                                  >
                                    {sharedFormatDetailValue(detail)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <View UNSAFE_className={styles.empty_state}>
          {t('no_timeline_entries', { ns: 'layout_components' })}
        </View>
      )}

      {canLoadMore ? (
        <div className={styles.see_more_wrap}>
          <Link isQuiet onPress={() => void loadTimeline(offset, true)} className={styles.btn_see_more}>
            {isLoading ? t('loading', { ns: 'layout_components', defaultValue: 'Loading...' }) : t('see_more', { ns: 'layout_components' })}
          </Link>
        </div>
      ) : null}
    </div>
  );
};

export default ContactTimelineView;
